import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import './FactionSelectPage.css';
import FactionsCarousel from "../../EmblaCarousel/FactionsCarousel";
import {RANDOM_FACTION_ID} from "../../../configs/factions";
import {getNetworkSocket} from "../../../utils/networkSocket";
import PageButton from "../../elements/PageButton/PageButton";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {getRankSkinFrameByRank} from '../../../utils/factions/getRankSkinFramePath';
import {getFactionSkinRank} from '../../../utils/factions/getFactionSkinRank';
import {useFactionSelectList} from "../../../hooks/factions/useFactionSelectList";
import {useFactionSelectPurchase} from "../../../hooks/factions/useFactionSelectPurchase";
import FactionSelectPurchaseModals, {
  renderFactionLockedSelectButton,
  TEXT_SHOP_LEAVES_LOBBY,
  TEXT_SHOP_LEAVES_LOCAL_MATCH,
} from "./FactionSelectPurchase";

const OPTIONS = {loop: true}

function FactionSelectPage({
                             goToMainLobbyScreen,
                             isRanked,
                             availableFactionIds,
                             bannedFactionId,
                             actingPlayerFactionId,
                             // Выбор в рейтинговом лобби подтвержден: сменить
                             // фракцию можно только после «Изменить выбор»
                             isSelectionLocked = false,
                             onFactionSelected,
                             // Выбор сделан, ритуал начался (сервер узнает о
                             // выборе через 1.8 с) — лобби начинает грузить арт
                             // новой фракции
                             onFactionSelectStart,
                             localMode,
                             onGoToCrystals,
                             onOpenInventory,
                           }) {
  const auth = useSelector((state) => state.private.auth);
  const [ritualPhase, setRitualPhase] = useState(null);

  // Покупка закрытой фракции прямо из карусели (за кристаллы или через
  // Сундук Вечности) — общая с десктопным лобби, см. useFactionSelectPurchase
  const purchase = useFactionSelectPurchase(auth);
  const {clearNotEnoughCrystals} = purchase;

  // Переход «За кристаллами» отсюда — это переход на другой экран целиком
  // (магазин), а не вкладка внутри магазина: для сетевого лобби это разрывает
  // WebSocket-соединение (LobbyPage размонтируется), для локальной игры —
  // сбрасывает несохранённый прогресс настройки (HotSeatPlayersInitPage
  // размонтируется). Предупреждаем явно прямо в модалке
  const insufficientCrystalsWarningText = localMode ? TEXT_SHOP_LEAVES_LOCAL_MATCH : TEXT_SHOP_LEAVES_LOBBY;

  const handleRitualStart = (faction) => {
    if (ritualPhase) return;
    // Подтвержденный выбор сервер сменить не даст — без ритуала обратно в
    // лобби, к кнопке «Изменить выбор»
    if (isSelectionLocked) {
      goToMainLobbyScreen();
      return;
    }
    onFactionSelectStart?.(faction.id);
    if (faction.id === RANDOM_FACTION_ID) {
      // У моковой фракции нет собственного звука выбора — только общее интро
      MENU_SOUND_MANAGER.playFactionSelectIntro();
    } else {
      MENU_SOUND_MANAGER.playFactionSelect(faction.id);
    }
    setRitualPhase('phase1');
    setTimeout(() => setRitualPhase('phase2'), 300);
    setTimeout(() => {
      setRitualPhase('phase3');
      if (onFactionSelected) {
        onFactionSelected(faction.id);
      } else {
        const ws = getNetworkSocket();
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'SELECT_FACTION',
            payload: {factionId: faction.id}
          }));
        }
      }
      goToMainLobbyScreen();
    }, 1800);
  };

  // Состав, порядок и доступность фракций — общие с десктопным лобби (см.
  // useFactionSelectList)
  const {
    slides,
    factionItems,
    checkIfFactionBanned,
    checkIfFactionAvailable,
    checkIfDetailsAvailable,
  } = useFactionSelectList({auth, availableFactionIds, isRanked, bannedFactionId, localMode});

  // Капс не нужен — его дает сама маска (.embla_faction-disable-mask)
  const getFactionDisableMaskLabel = (faction) => {
    if (checkIfFactionBanned(faction)) return 'Забанена';
    return faction.implemented ? 'Не открыт' : 'В разработке';
  };

  // Заменяет заблокированную кнопку выбора для недоступных фракций: покупка
  // за кристаллы или «Получить» (см. renderFactionLockedSelectButton)
  const renderFactionSelectButton = (item) => renderFactionLockedSelectButton({
    item,
    isAvailable: checkIfFactionAvailable(item),
    isBanned: checkIfFactionBanned(item),
    purchase,
    pageId: 'FactionSelectPage',
    className: 'embla_select-button',
  });

  return (
    <>
      <FactionsCarousel
        slides={slides}
        items={factionItems}
        options={OPTIONS}
        onRitualStart={handleRitualStart}
        onSelectedIndexChange={clearNotEnoughCrystals}
        ritualPhase={ritualPhase}
        actingPlayerFactionId={actingPlayerFactionId}
        getItemImagePath={(item) => item.imagePath}
        // Выбранный игроком рейтинговый образ идет в рамке своей лиги — так
        // же, как в карусели образов на странице фракции. У «Случайной
        // фракции» образа нет, и рамки тоже
        getItemFramePath={(item) => getRankSkinFrameByRank(item.rank)}
        // Выбранное обычное обличие — с шевронами своего рейтинга. id
        // элемента здесь — фракция, а обличие лежит в skinId
        getItemSkinRank={(item) => getFactionSkinRank(item.skinId, item.id)}
        getItemName={(item) => item.name}
        getItemTextColor={(item) => item.textColor}
        // getItemImageContainerStyle={(item) => item.imageContainerStyle}
        isItemAvailable={checkIfFactionAvailable}
        checkIfSelectionAvailable={checkIfFactionAvailable}
        checkIfDetailsAvailable={checkIfDetailsAvailable}
        disableMaskLabel={getFactionDisableMaskLabel}
        renderSelectButton={renderFactionSelectButton}
        className={'faction-select-page_carousel'}
      />

      <div className={`main-menu-buttons ${ritualPhase ? 'ritual-siblings--fading' : ''}`}>
        <PageButton
          className="main-menu-button" onClick={goToMainLobbyScreen} pageId="FactionSelectPage" buttonId="back"
        >Назад</PageButton>
      </div>

      <FactionSelectPurchaseModals
        purchase={purchase}
        insufficientCrystalsWarningText={insufficientCrystalsWarningText}
        onGoToCrystals={onGoToCrystals}
        onOpenInventory={onOpenInventory}
      />
    </>
  );
}

export default FactionSelectPage;
